'use client';

import React, { useEffect, useRef, useState } from 'react';

const mapPoints = [
  {
    id: 'pt-church',
    number: '1',
    name: 'St Petrox Church',
    note: 'Wedding Ceremony',
    color: '#2C3E50',
    lat: 50.34241013290033,
    lng: -3.566617470378521,
    mapsQuery: 'St+Petrox+Church+Dartmouth',
    labelBelow: false,
  },
  {
    id: 'pt-boathouse',
    number: '2',
    name: 'Gullsway Boathouse',
    note: 'Wedding Reception',
    color: '#7A8C5E',
    lat: 50.34693041853506,
    lng: -3.577034926222497,
    mapsQuery: 'Gullsway+Boathouse+Dartmouth',
    labelBelow: false,
  },
  {
    id: 'pt-sugary',
    number: '3',
    name: 'Castle Cove',
    note: 'Post-ceremony',
    color: '#C8B89A',
    lat: 50.34112142607876,
    lng: -3.565694487875412,
    mapsQuery: 'Castle+Cove+Dartmouth',
    labelBelow: true,
  },
  {
    id: 'pt-blackpool',
    number: '4',
    name: 'Blackpool Sands',
    note: 'Sunday Brunch & Saunas',
    color: '#8C7B6B',
    lat: 50.31948600621138,
    lng: -3.6104980210008866,
    mapsQuery: 'Blackpool+Sands+Dartmouth',
    labelBelow: false,
  },
  {
    id: 'pt-embankment',
    number: '5',
    name: 'The Embankment',
    note: 'Town centre & ferry',
    color: '#B5BFA8',
    lat: 50.351348526520994,
    lng: -3.577089529338521,
    mapsQuery: 'Dartmouth+Embankment',
    labelBelow: false,
  },
  {
    id: 'pt-parkride',
    number: '6',
    name: 'Dartmouth Park & Ride',
    note: 'Park & Ride',
    color: '#5B8DB8',
    lat: 50.346742317138364,
    lng: -3.6021337822150272,
    mapsQuery: 'Dartmouth+Park+and+Ride',
    labelBelow: false,
  },
];

const MAP_CENTER = { lat: 50.334, lng: -3.588 };
const ZOOM = 13;

function lngToTileX(lng: number, zoom: number) {
  return ((lng + 180) / 360) * Math.pow(2, zoom);
}

function latToTileY(lat: number, zoom: number) {
  const latRad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * Math.pow(2, zoom);
}

function latLngToPixel(
  lat: number,
  lng: number,
  centerLat: number,
  centerLng: number,
  zoom: number,
  mapWidth: number,
  mapHeight: number
) {
  const tileSize = 256;
  const centerX = lngToTileX(centerLng, zoom) * tileSize;
  const centerY = latToTileY(centerLat, zoom) * tileSize;
  const pointX = lngToTileX(lng, zoom) * tileSize;
  const pointY = latToTileY(lat, zoom) * tileSize;
  return {
    x: mapWidth / 2 + (pointX - centerX),
    y: mapHeight / 2 + (pointY - centerY),
  };
}

export default function DartmouthMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapSize, setMapSize] = useState({ width: 800, height: 480 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const updateSize = () => {
      if (mapRef.current) {
        setMapSize({
          width: mapRef.current.offsetWidth,
          height: mapRef.current.offsetHeight,
        });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const tileSize = 256;
  const centerTileX = lngToTileX(MAP_CENTER.lng, ZOOM);
  const centerTileY = latToTileY(MAP_CENTER.lat, ZOOM);
  const tilesX = Math.ceil(mapSize.width / tileSize) + 2;
  const tilesY = Math.ceil(mapSize.height / tileSize) + 2;
  const startTileX = Math.floor(centerTileX) - Math.floor(tilesX / 2);
  const startTileY = Math.floor(centerTileY) - Math.floor(tilesY / 2);
  const offsetX = mapSize.width / 2 - (centerTileX - Math.floor(centerTileX)) * tileSize - Math.floor(tilesX / 2) * tileSize;
  const offsetY = mapSize.height / 2 - (centerTileY - Math.floor(centerTileY)) * tileSize - Math.floor(tilesY / 2) * tileSize;

  const tiles: { tx: number; ty: number; x: number; y: number }[] = [];
  for (let i = 0; i < tilesX; i++) {
    for (let j = 0; j < tilesY; j++) {
      tiles.push({
        tx: startTileX + i,
        ty: startTileY + j,
        x: offsetX + i * tileSize,
        y: offsetY + j * tileSize,
      });
    }
  }

  return (
    <section className="py-24 px-8 md:px-16 xl:px-24 border-t border-border">
      <div className="max-w-screen-2xl mx-auto">
        <div className="mb-12">
          <p className="section-label mb-4">Find Your Way</p>
          <h2 className="font-serif text-display-md text-faded-navy mb-6">
            Dartmouth &amp; Surrounds
          </h2>
          <p className="font-sans text-sm text-stone max-w-xl leading-relaxed">
            All the key locations for the wedding weekend, mapped for you. Click any marker for directions.
          </p>
        </div>

        {/* Legend */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-8 flex-wrap">
          {mapPoints.map((pt) => (
            <a
              key={pt.id}
              href={`https://www.google.com/maps/search/?api=1&query=${pt.mapsQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 group"
            >
              <div
                className="flex items-center justify-center rounded-full shrink-0 text-white font-bold text-sm"
                style={{
                  backgroundColor: pt.color,
                  width: '30px',
                  height: '30px',
                  marginTop: '2px',
                }}
              >
                {pt.number}
              </div>
              <div>
                <p className="font-sans text-sm text-faded-navy font-semibold leading-tight group-hover:underline">
                  {pt.name}
                </p>
                <p className="font-sans text-xs text-stone mt-0.5">{pt.note}</p>
              </div>
            </a>
          ))}
        </div>

        {/* Map */}
        <div
          ref={mapRef}
          className="w-full rounded-sm overflow-hidden border border-border relative"
          style={{ height: '520px', background: '#e8e0d8' }}
          aria-label="Map of Dartmouth showing wedding venues and points of interest"
        >
          {/* OSM Tile layer */}
          {mounted && tiles.map((tile) => {
            const maxTile = Math.pow(2, ZOOM);
            const wrappedTx = ((tile.tx % maxTile) + maxTile) % maxTile;
            return (
              <img
                key={`${tile.tx}-${tile.ty}`}
                src={`https://tile.openstreetmap.org/${ZOOM}/${wrappedTx}/${tile.ty}.png`}
                alt=""
                style={{
                  position: 'absolute',
                  left: tile.x,
                  top: tile.y,
                  width: tileSize,
                  height: tileSize,
                  imageRendering: 'crisp-edges',
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
                draggable={false}
              />
            );
          })}

          {/* Markers */}
          {mounted && mapPoints.map((pt) => {
            const pos = latLngToPixel(
              pt.lat,
              pt.lng,
              MAP_CENTER.lat,
              MAP_CENTER.lng,
              ZOOM,
              mapSize.width,
              mapSize.height
            );
            return (
              <a
                key={pt.id}
                href={`https://www.google.com/maps/search/?api=1&query=${pt.mapsQuery}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  position: 'absolute',
                  left: pos.x,
                  top: pos.y,
                  transform: pt.labelBelow ? 'translate(-50%, -50%)' : 'translate(-50%, -100%)',
                  zIndex: 10,
                  textDecoration: 'none',
                }}
                title={pt.name}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Label above (default) */}
                  {!pt.labelBelow && (
                    <div
                      style={{
                        marginBottom: '4px',
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        color: '#2C3E50',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: '3px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      {pt.name}
                    </div>
                  )}
                  <div
                    style={{
                      backgroundColor: pt.color,
                      color: '#fff',
                      borderRadius: '50% 50% 50% 0',
                      transform: 'rotate(-45deg)',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                      border: '2px solid #fff',
                    }}
                  >
                    <span
                      style={{
                        transform: 'rotate(45deg)',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        lineHeight: 1,
                      }}
                    >
                      {pt.number}
                    </span>
                  </div>
                  {/* Label below */}
                  {pt.labelBelow && (
                    <div
                      style={{
                        marginTop: '4px',
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        color: '#2C3E50',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: '3px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      {pt.name}
                    </div>
                  )}
                </div>
              </a>
            );
          })}

          {/* OSM attribution */}
          <div
            style={{
              position: 'absolute',
              bottom: 4,
              right: 6,
              fontSize: '10px',
              color: '#555',
              background: 'rgba(255,255,255,0.7)',
              padding: '1px 4px',
              borderRadius: '2px',
              zIndex: 20,
            }}
          >
            © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" style={{ color: '#555' }}>OpenStreetMap</a> contributors
          </div>
        </div>

        {/* Direct links to each location */}
        <div className="flex flex-wrap gap-4 mt-5">
          {mapPoints.map((pt) => (
            <a
              key={pt.id}
              href={`https://www.google.com/maps/search/?api=1&query=${pt.mapsQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-sans font-medium text-stone hover:text-faded-navy transition-colors"
            >
              <span
                className="inline-flex items-center justify-center rounded-full text-white font-bold"
                style={{ backgroundColor: pt.color, width: '18px', height: '18px', fontSize: '10px' }}
              >
                {pt.number}
              </span>
              {pt.name} ↗
            </a>
          ))}
        </div>

        <p className="text-xs font-sans text-stone/50 mt-3">
          Click a location to open directions in Google Maps.
        </p>

        {/* Getting here */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-20 pt-16 border-t border-border">
          {[
            {
              id: 'travel-car',
              label: 'By Car',
              content: 'Dartmouth is approximately 3 hours from London via the M5 and A38. The postcode for the town centre is TQ6 9PH. Please note that Mayors Avenue car park will be closed for the weekend due to the Regatta, so please plan your parking arrangements in advance. We recommend using the Park and Ride, which is a well maintained and secure site. Note: roads around Dartmouth Castle are narrow, allow extra time.',
            },
            {
              id: 'travel-train',
              label: 'By Train',
              content: 'The nearest mainline station is Totnes (approximately 12 miles). From Totnes you can take a taxi or the scenic Dartmouth Steam Railway to Kingswear, then the passenger ferry across to Dartmouth. The journey from London Paddington to Totnes takes approximately 2.5 hours.',
            },
            {
              id: 'travel-ferry',
              label: 'By Ferry',
              content: 'The passenger ferry runs regularly between Dartmouth Embankment and Kingswear throughout the day and evening. It is the most charming way to arrive. A higher car ferry also operates nearby at Dartmouth–Kingswear Higher Ferry on the A3122.',
            },
          ].map((info) => (
            <div key={info.id}>
              <p className="section-label mb-4">{info.label}</p>
              <p className="font-sans text-sm text-stone leading-relaxed">{info.content}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}